import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import quickFetch from 'quick-fetch';
import useHandleQueryParams from 'framework-listing-lib/internal/deepLink/hooks/useHandleQueryParams';
import useDeepEqualEffect from 'framework-listing-lib/internal/hooks/useDeepEqualEffect';
import { useCurrentUIMode, useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import { useListingLibData } from 'framework-listing-lib/internal/providers/DataProvider';
import { useUpdateViewsCache } from 'framework-listing-lib/internal/ViewTabs/utils/cache';
import { useCurrentView, useHasViews, useOnViewIdChange, useViewPropsCurrentViewId } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { getViewId } from 'framework-listing-lib/internal/ViewTabs/utils/views';
import { jsx as _jsx } from "react/jsx-runtime";
const ViewsContext = /*#__PURE__*/createContext(undefined);
export default function ViewsProvider({
  children,
  onChangeUIMode
}) {
  const currentUIMode = useCurrentUIMode();
  const isFoldersMode = useIsFoldersMode();
  const urlParams = useHandleQueryParams();
  const currentView = useCurrentView();
  const hasViews = useHasViews();
  const onViewIdChange = useOnViewIdChange();
  const currentViewId = useViewPropsCurrentViewId();
  const updateViewsCache = useUpdateViewsCache();

  /**
   * Global column state.
   * Reads from @columnKeys prop sent to CrmListingTable
   */
  const [columns, setColumns] = useState([]);
  const [numberOfFrozenColumns, setNumberOfFrozenColumns] = useState(undefined);
  const [cardProperties, setCardProperties] = useState([]);
  const [quickFilterPropertyNames, setQuickFilterPropertyNames] = useState([]);
  const [viewUpdateState, setViewUpdateState] = useState({
    changedCardProperties: false,
    changedColumns: false,
    changedFilters: false,
    changedQuickFilterPropertyNames: false,
    changedSort: false,
    changedUIMode: false,
    mLangGroup: {
      changed: false,
      showAll: false
    }
  });
  const {
    loading
  } = useListingLibData();
  const value = useMemo(() => {
    return {
      cardProperties,
      columns,
      isCurrentViewModified: viewUpdateState.changedColumns || viewUpdateState.changedFilters || viewUpdateState.changedSort || viewUpdateState.changedUIMode || viewUpdateState.mLangGroup.changed || viewUpdateState.changedCardProperties || viewUpdateState.changedQuickFilterPropertyNames,
      // view is only ready after we set columns
      isLoading: !isFoldersMode && hasViews && (loading || columns.length === 0),
      numberOfFrozenColumns,
      quickFilterPropertyNames,
      setCardProperties,
      setColumns,
      setNumberOfFrozenColumns,
      setQuickFilterPropertyNames,
      setViewUpdateState,
      updateViewsCache,
      viewUpdateState
    };
  }, [cardProperties, columns, hasViews, isFoldersMode, loading, numberOfFrozenColumns, quickFilterPropertyNames, updateViewsCache, viewUpdateState]);

  /**
   * Update @columns when:
   *  1 - All views have been fetched
   *  2 - Current view changes
   */
  useDeepEqualEffect(() => {
    var _currentView$quickFil, _currentView$quickFil2;
    if (!hasViews || loading || !currentView) {
      return;
    }
    setColumns(currentView.columns.map(column => column.name));
    setQuickFilterPropertyNames((_currentView$quickFil = (_currentView$quickFil2 = currentView.quickFilterProperties) === null || _currentView$quickFil2 === void 0 ? void 0 : _currentView$quickFil2.map(({
      name
    }) => name)) !== null && _currentView$quickFil !== void 0 ? _currentView$quickFil : []);
    setNumberOfFrozenColumns(currentView.state.numberOfFrozenColumns || 0);
    setCardProperties(currentView.state.cardProperties || []);

    /**
     * Notify consumers when the current view's uiMode is different than the current UI mode being shown.
     */
    if (currentView.state.uiMode && currentUIMode !== currentView.state.uiMode && onChangeUIMode && !viewUpdateState.changedUIMode) {
      onChangeUIMode(currentView.state.uiMode);
    }
    setViewUpdateState({
      changedCardProperties: false,
      changedColumns: false,
      changedFilters: false,
      changedQuickFilterPropertyNames: false,
      changedSort: false,
      changedUIMode: Boolean(currentView.state.uiMode && currentUIMode !== currentView.state.uiMode),
      mLangGroup: {
        changed: false,
        showAll: currentView.state && currentView.state.mLangGroup ? currentView.state.mLangGroup.showAll : false
      }
    });
  }, [currentUIMode, currentView, hasViews, loading, onChangeUIMode, viewUpdateState.changedUIMode]);

  /**
   * Update columns from query string
   */
  const [hasUsedQueryParams, setHasUsedQueryParams] = useState(false);
  useEffect(() => {
    if (loading || !urlParams || hasUsedQueryParams) {
      return;
    }
    if (!urlParams.columns.length) {
      return;
    }
    setColumns(urlParams.columns);
    setViewUpdateState(prev => Object.assign({}, prev, {
      changedColumns: true
    }));
    setHasUsedQueryParams(true);
  }, [hasUsedQueryParams, loading, urlParams]);

  // If the view id in the url doesn't match the view being shown,
  // change to the one in the url.
  useDeepEqualEffect(() => {
    if (isFoldersMode || loading || !currentView || !hasViews) {
      return;
    }

    //When changing to a different view on initial page load, quick fetch
    //potentially caches data for the incorrect view. This will check
    //if this is the case and remove the cache
    const isSameViewId = currentViewId === currentView.id;
    const isSameViewIdLabel = Boolean(currentView.idLabel && currentView.idLabel === currentViewId);
    const shouldRedirect = !isSameViewId && !isSameViewIdLabel;
    if (shouldRedirect) {
      quickFetch.removeEarlyRequest(`fetch-crm-objects-${currentView.objectTypeId}`);
      Metrics.counter('ignore-quick-fetch-cache').increment();
      onViewIdChange(getViewId(currentView), {
        redirect: true
      });
    }
  }, [currentView, currentViewId, hasViews, isFoldersMode, loading, onViewIdChange]);
  return /*#__PURE__*/_jsx(ViewsContext.Provider, {
    value: value,
    children: children
  });
}
export function useViews() {
  const context = useContext(ViewsContext);
  if (context === undefined) {
    throw new Error('useViews must be used within a ViewsProvider');
  }
  return context;
}